<template>
  <section class="bjp-hero" aria-labelledby="hero-title">
    <div class="hero-orb hero-orb--one" aria-hidden="true"></div>
    <div class="hero-orb hero-orb--two" aria-hidden="true"></div>

    <div class="container hero-layout">
      <div class="hero-copy">
        <span class="hero-kicker">
          <i class="bx bxs-badge-check" aria-hidden="true"></i>
          Portal oficial · Gestión 2026
        </span>

        <div class="hero-title-group">
          <h1 id="hero-title">
            Bono
            <span>Juancito Pinto</span>
          </h1>

          <div class="progress-panel" role="status" aria-live="polite">
            <strong class="progress-value">{{ loading ? "--" : animatedPercentage }}%</strong>
          </div>
        </div>

        <p class="hero-description">
          Consulta de forma segura si eres beneficiario y accede a toda la
          información oficial del programa en un solo lugar.
        </p>

        <div class="hero-actions">
          <router-link :to="{ name: 'public-search' }" class="hero-button hero-button--primary">
            Consultar beneficio
            <i class="bx bx-right-arrow-alt" aria-hidden="true"></i>
          </router-link>
          <router-link :to="{ name: 'public-info' }" class="hero-button hero-button--ghost">
            Ver requisitos
          </router-link>
        </div>

        <div class="hero-meta" aria-label="Características del portal">
          <span style="color: white;"><i style="color: white;" class="bx bx-shield-quarter"></i> Consulta segura</span>
          <span style="color: white;"><i style="color: white;" class="bx bx-check-circle"></i> Información oficial</span>
        </div>
      </div>

      <div class="hero-visual">
        <div class="logo-stage">
          <span class="logo-ring" aria-hidden="true"></span>
          <img
            src="@/assets/bjp/logo-juancito-pinto-2026.png"
            class="hero-logo"
            alt="Bono Juancito Pinto 2026"
            decoding="async"
          />
        </div>

      </div>
    </div>

    <a href="#accesos" class="scroll-cue" aria-label="Ir a accesos rápidos">
      <span style="color: white;">Explorar portal</span>
      <i style="color: white;" class="bx bx-down-arrow-alt" aria-hidden="true"></i>
    </a>
  </section>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import BjpService from "../../services/BjpService";

const percentage = ref(0);
const animatedPercentage = ref(0);
const loading = ref(true);
let animationFrameId = null;

const animatePercentage = (target) => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    animatedPercentage.value = target;
    return;
  }

  const duration = 1200;
  const startTime = performance.now();
  const update = (currentTime) => {
    const elapsed = Math.min((currentTime - startTime) / duration, 1);
    animatedPercentage.value = Math.round(target * (1 - Math.pow(1 - elapsed, 3)));
    if (elapsed < 1) animationFrameId = requestAnimationFrame(update);
  };
  animationFrameId = requestAnimationFrame(update);
};

onMounted(async () => {
  try {
    const response = await BjpService.getGlobalPercentage();
    const parsed = Number.parseFloat(response.data.percentage);
    percentage.value = Number.isFinite(parsed)
      ? Math.min(Math.max(Math.round(parsed), 0), 100)
      : 0;
  } catch (error) {
    percentage.value = 0;
  } finally {
    loading.value = false;
    animatePercentage(percentage.value);
  }
});

onUnmounted(() => {
  if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
});
</script>

<style scoped>
.bjp-hero {
  position: relative;
  display: flex;
  min-height: calc(100vh - 76px);
  align-items: center;
  overflow: hidden;
  padding: clamp(4.5rem, 8vw, 7.5rem) 0 clamp(5.5rem, 8vw, 7rem);
  isolation: isolate;
}

.bjp-hero::before {
  position: absolute;
  inset: 0;
  z-index: -2;
  content: "";
  background: linear-gradient(90deg, rgba(197, 167, 70, 0.14), transparent 48%),
    linear-gradient(180deg, transparent 65%, rgba(6, 47, 54, 0.18));
}

.hero-orb {
  position: absolute;
  z-index: -1;
  border: 1px solid rgba(230, 203, 114, 0.22);
  border-radius: 50%;
  pointer-events: none;
}

.hero-orb--one {
  display: none;
}

.hero-orb--two {
  bottom: -15rem;
  left: 27%;
  width: 27rem;
  height: 27rem;
  background: rgba(230, 203, 114, 0.08);
}

.hero-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(400px, 0.85fr);
  align-items: center;
  gap: clamp(1.5rem, 4vw, 5rem);
}

.hero-copy {
  position: relative;
  z-index: 2;
  max-width: 660px;
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  margin-bottom: 1.25rem;
  padding: 0.48rem 0.78rem;
  color: #ffffff;
  font-size: 0.76rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 999px;
  background: rgba(6, 47, 54, 0.28);
  gap: 0.45rem;
}

.hero-kicker i {
  font-size: 1rem;
}

.hero-title-group {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px;
  align-items: end;
  gap: 1.1rem;
}

.hero-copy h1 {
  margin: 0;
  color: var(--bjp-petrol-950);
  font-size: clamp(3.6rem, 6.4vw, 6.2rem);
  font-weight: 850;
  line-height: 0.88;
  letter-spacing: -0.065em;
}

.hero-copy h1 span {
  display: block;
  margin-top: 0.2em;
  color: rgba(6, 47, 54, 0.72);
  font-size: 0.68em;
  font-weight: 700;
  letter-spacing: -0.045em;
}

.hero-description {
  max-width: 590px;
  margin: 1.75rem 0 0;
  color: #ffffff;
  font-size: clamp(1rem, 1.5vw, 1.14rem);
  font-weight: 450;
  line-height: 1.75;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  margin-top: 2rem;
  gap: 0.8rem;
}

.hero-button {
  display: inline-flex;
  min-height: 52px;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.35rem;
  font-size: 0.9rem;
  font-weight: 750;
  border-radius: 14px;
  gap: 0.55rem;
  transition: transform 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease;
}

.hero-button:hover {
  text-decoration: none;
  transform: translateY(-2px);
}

.hero-button--primary {
  color: #ffffff;
  background: var(--bjp-petrol-950);
  box-shadow: 0 14px 30px rgba(6, 47, 54, 0.22);
}

.hero-button--primary:hover {
  color: #ffffff;
  background: var(--bjp-petrol-800);
}

.hero-button--primary i {
  font-size: 1.3rem;
}

.hero-button--ghost {
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.88);
  background: rgba(6, 47, 54, 0.24);
}

.hero-button--ghost:hover {
  color: #ffffff;
  border-color: #ffffff;
  background: rgba(6, 47, 54, 0.4);
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  color: rgba(6, 47, 54, 0.72);
  font-size: 0.78rem;
  font-weight: 650;
  gap: 1.2rem;
}

.hero-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.hero-meta i {
  color: var(--bjp-petrol-800);
  font-size: 1rem;
}

.hero-visual {
  position: relative;
  display: flex;
  min-height: 640px;
  align-items: center;
  justify-content: center;
}

.logo-stage {
  position: relative;
  display: grid;
  width: min(100%, 660px);
  aspect-ratio: 1 / 1;
  place-items: center;
}

.logo-stage::before {
  position: absolute;
  inset: 8%;
  content: "";
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(6, 47, 54, 0.2) 0%,
    rgba(6, 47, 54, 0.11) 58%,
    rgba(6, 47, 54, 0.03) 72%,
    transparent 73%
  );
}

.logo-ring {
  position: absolute;
  inset: 1%;
  border: 1px dashed rgba(255, 255, 255, 0.88);
  border-radius: 50%;
  animation: rotate-ring 36s linear infinite;
}

.hero-logo {
  position: relative;
  z-index: 1;
  width: 88%;
  max-height: 620px;
  object-fit: contain;
  filter: drop-shadow(0 24px 32px rgba(52, 45, 24, 0.2));
  animation: logo-in 0.8s ease-out both;
}

.progress-panel {
  display: flex;
  align-items: center;
  align-self: stretch;
  justify-content: center;
}

.progress-value {
  color: #ffffff;
  font-size: clamp(4.5rem, 8vw, 7.6rem);
  font-weight: 850;
  font-variant-numeric: tabular-nums;
  line-height: 0.9;
  letter-spacing: -0.07em;
  text-shadow: 0 12px 30px rgba(6, 47, 54, 0.2);
}

.scroll-cue {
  position: absolute;
  bottom: 1.25rem;
  left: 50%;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  color: rgba(6, 47, 54, 0.62);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transform: translateX(-50%);
}

.scroll-cue:hover {
  color: var(--bjp-petrol-950);
  text-decoration: none;
}

.scroll-cue i {
  font-size: 1.25rem;
  animation: cue 1.8s ease-in-out infinite;
}

@keyframes rotate-ring {
  to { transform: rotate(360deg); }
}

@keyframes cue {
  50% { transform: translateY(5px); }
}

@keyframes logo-in {
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 991px) {
  .bjp-hero {
    min-height: auto;
    padding: 3.5rem 0 4rem;
  }
  .hero-layout {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  .hero-copy {
    max-width: 720px;
    text-align: center;
    justify-self: center;
  }
  .hero-description {
    margin-right: auto;
    margin-left: auto;
  }
  .hero-actions,
  .hero-meta {
    justify-content: center;
  }
  .hero-visual {
    min-height: 480px;
  }
  .logo-stage {
    width: min(86vw, 580px);
  }
  .scroll-cue {
    display: none;
  }
}

@media (max-width: 575px) {
  .bjp-hero {
    padding: 2.25rem 0 3rem;
  }
  .hero-layout {
    gap: 0;
  }
  .hero-kicker {
    margin-bottom: 1rem;
    padding: 0.42rem 0.65rem;
    font-size: 0.67rem;
    letter-spacing: 0.05em;
  }
  .hero-copy h1 {
    font-size: clamp(2.8rem, 15vw, 4rem);
    line-height: 0.94;
  }
  .hero-title-group {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }
  .hero-description {
    margin-top: 1.25rem;
    font-size: 0.94rem;
    line-height: 1.65;
  }
  .hero-actions {
    flex-direction: column;
    margin-top: 1.5rem;
    gap: 0.65rem;
  }
  .hero-button {
    width: 100%;
    min-height: 48px;
    padding: 0.75rem 1rem;
  }
  .hero-meta {
    margin-top: 1.2rem;
    gap: 0.7rem;
  }
  .hero-visual {
    min-height: 0;
    margin-top: 2rem;
  }
  .logo-stage {
    width: min(82vw, 340px);
  }
  .progress-panel {
    width: min(100%, 260px);
    margin: 0 auto;
  }
  .progress-value {
    font-size: clamp(3.5rem, 20vw, 5rem);
  }
}

@media (max-width: 400px) {
  .bjp-hero {
    padding: 1.75rem 0 2.5rem;
  }
  .bjp-hero .container {
    padding-right: 0.85rem;
    padding-left: 0.85rem;
  }
  .hero-kicker {
    white-space: normal;
  }
  .hero-copy h1 {
    font-size: clamp(2.5rem, 14vw, 3.25rem);
  }
  .hero-copy h1 span {
    letter-spacing: -0.025em;
  }
  .hero-meta {
    flex-direction: column;
    align-items: center;
  }
  .logo-stage {
    width: min(78vw, 285px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-logo,
  .logo-ring,
  .scroll-cue i {
    animation: none;
  }
}
</style>
